function WKTtoParsePoint(wkt) {
    const regex = /^POINT\s*\(([^()]+)\)$/;
    const match = wkt.match(regex);

    if (!match) {
        throw new Error('Invalid WKT Point format');
    }
    const coords = match[1].split(' ').map(Number);
    return coords; // 返回点的坐标数组 [x, y]
}

function WKTtoParseLineString(wkt) {
    // 去除WKT字符串前后的空格
    wkt = wkt.trim();
    // 确认字符串以'LINESTRING'开头
    if (!wkt.startsWith('LINESTRING')) {
        throw new Error('Invalid WKT string: must start with "LINESTRING"');
    }
    // 提取坐标部分，即括号内的内容
    const coordinatesStr = wkt.slice(wkt.indexOf('(') + 1, wkt.lastIndexOf(')')).trim();
    // 处理边界情况：空的 LineString
    if (!coordinatesStr) {
        return [];
    }

    // 将坐标字符串按逗号分隔成单独的坐标对
    const coordinatePairs = coordinatesStr.split(',');

    // 将每个坐标对转换成经纬度数组，并返回所有坐标的数组
    return coordinatePairs.map((pair) => {
        // 关键修复：先去除坐标对首尾的空格，避免拆分出空字符串
        const trimmedPair = pair.trim();
        // 按空格拆分（兼容多个连续空格的情况，用正则 \s+ 匹配）
        const [lon, lat] = trimmedPair.split(/\s+/).map(Number);
        return [lon, lat];
    });
}

/**
 * 把 Polygon 类型 WKT 字符串转换为二维坐标数组
 * @param {String} wkt Polygon 格式 WKT 字符串
 * @returns {Array} 二维坐标数组 [[lng1, lat1], [lng2, lat2], ...]
 */
function WKTtoParsePolygon(wkt) {
    // 1. 去除字符串前后多余空格
    wkt = wkt.trim();

    // 2. 验证是否为 POLYGON 格式 WKT
    if (!wkt.startsWith('POLYGON')) {
        throw new Error('Invalid WKT string: must start with "POLYGON"');
    }

    // 3. 提取内层括号中的坐标字符串（处理双层括号：((...))）
    // 先找到第一个左括号和最后一个右括号，去除外层括号
    const outerLeft = wkt.indexOf('(');
    const outerRight = wkt.lastIndexOf(')');
    const innerWkt = wkt.slice(outerLeft + 1, outerRight).trim();

    // 再找到内层的第一个左括号和最后一个右括号，提取核心坐标字符串
    const innerLeft = innerWkt.indexOf('(');
    const innerRight = innerWkt.lastIndexOf(')');
    if (innerLeft === -1 || innerRight === -1) {
        throw new Error('Invalid Polygon WKT: missing inner brackets');
    }
    const coordinatesStr = innerWkt.slice(innerLeft + 1, innerRight).trim();

    // 4. 处理边界情况：空坐标集合
    if (!coordinatesStr) {
        return [];
    }

    // 5. 拆分坐标对（按逗号分隔）
    const coordinatePairs = coordinatesStr.split(',');

    // 6. 转换每个坐标对为 [lng, lat] 数字数组
    return coordinatePairs.map((pair) => {
        // 去除坐标对首尾空格，兼容多空格分隔
        const trimmedPair = pair.trim();
        const [lon, lat] = trimmedPair.split(/\s+/).map(Number);

        // 校验数值有效性，避免 NaN
        if (isNaN(lon) || isNaN(lat)) {
            throw new Error(`Invalid coordinate pair: ${pair} (cannot convert to valid number)`);
        }

        return [lon, lat];
    });
}

// 函数：将点保存为WKT格式
function pointToWKT(longitude, latitude) {
    return `POINT (${longitude} ${latitude})`;
}

// 函数：将线串保存为WKT格式
function lineStringToWKT(geometry) {
    // 1. 参数校验，确保坐标数组有效（折线至少需要2个顶点）
    if (!Array.isArray(geometry) || geometry.length < 2) {
        throw new Error('折线坐标无效，至少需要提供 2 个顶点坐标');
    }

    // 2. 处理每个坐标：将 [lng, lat] 转为 "lng lat" 格式（保留 12 位小数控制精度）
    const coordList = geometry.map((coord) => {
        const longitude = Number(coord[0]).toFixed(12);
        const latitude = Number(coord[1]).toFixed(12);
        return `${longitude} ${latitude}`;
    });

    // 3. 拼接最终的 WKT 字符串（LineString 仅需单层括号）
    return `LINESTRING (${coordList.join(', ')})`;
}

// 函数：将多边形保存为WKT格式（注意多边形是封闭的，所以最后一个点应该与第一个点相同，这里不自动添加）
function polygonToWKT(geometry) {
    // 1. 参数校验：确保坐标数组有效（多边形至少需要3个不重复顶点，闭合后至少4个元素）
    if (!Array.isArray(geometry) || geometry.length < 4) {
        throw new Error('多边形坐标无效，至少需要提供 3 个不重复顶点并闭合');
    }

    // 2. 处理每个坐标：将 [lng, lat] 转为 "lng lat" 格式（保留 12 位小数控制精度）
    const coordList = geometry.map((coord) => {
        const longitude = Number(coord[0]).toFixed(12);
        const latitude = Number(coord[1]).toFixed(12);
        return `${longitude} ${latitude}`;
    });

    // 3. 拼接最终的 WKT 字符串（Polygon 要求双层括号，你的坐标已闭合，无需额外处理）
    return `POLYGON ((${coordList.join(', ')}))`;
}

// 函数：将圆（通过圆心和半径）保存为近似多边形的WKT格式
function circleToWKT(center, radius, numPoints = 36) {
    let points = [];
    for (let i = 0; i < numPoints; i++) {
        let angle = (i / numPoints) * 2 * Math.PI; // 计算每个点的角度
        let x = center[0] + radius * Math.cos(angle); // 计算x坐标
        let y = center[1] + radius * Math.sin(angle); // 计算y坐标
        points.push([x, y]); // 将坐标添加到点数组中
    }
    // 闭合多边形（使第一个点和最后一个点相同）
    points.push(points[0]);

    // 转换为WKT格式的POLYGON字符串
    let wkt = `POLYGON ((${points.map((coord) => coord.join(' ')).join(', ')}))`;
    return wkt;
}

// 解析 MULTIPOLYGON 字符串
function parseMultiPolygon(multiPolygonStr, type) {
    let polygonType = type === 'MULTIPOLYGON' ? /MULTIPOLYGON\s*\(\(\((.*?)\)\)\)/ : /POLYGON\s*\(\((.*?)\)\)/;

    // 提取坐标部分
    const coordsPart = multiPolygonStr.match(polygonType)[1];
    // 分割每个坐标点
    const points = coordsPart.split(',');

    // 提取经纬度
    const coordinates = points.map((point) => {
        const [lng, lat] = point.trim().split(/\s+/);
        return [Number(lng), Number(lat)];
    });

    return coordinates;
}

// 计算中心点
function getPointsCenter(points) {
    let pointNum = points.length; // 坐标点个数
    let X = 0;
    let Y = 0;
    let Z = 0;
    for (let i = 0; i < points.length; i++) {
        let lat;
        let lng;
        let x;
        let y;
        let z;
        lat = (parseFloat(points[i][1]) * Math.PI) / 180;
        lng = (parseFloat(points[i][0]) * Math.PI) / 180;
        x = Math.cos(lat) * Math.cos(lng);
        y = Math.cos(lat) * Math.sin(lng);
        z = Math.sin(lat);
        X += x;
        Y += y;
        Z += z;
    }
    X /= pointNum;
    Y /= pointNum;
    Z /= pointNum;

    let tmpLng = Math.atan2(Y, X);
    let tmpLat = Math.atan2(Z, Math.sqrt(X * X + Y * Y));

    return { latitude: (tmpLat * 180) / Math.PI, longitude: (tmpLng * 180) / Math.PI };
}

/**
 * 自动闭合多边形坐标（确保首尾点相同）
 * @param {Array} coords 多边形坐标数组（如 [[x1,y1], [x2,y2], ...]）
 * @returns {Array} 闭合后的坐标数组
 */
function closePolygonCoords(coords) {
    if (!coords || coords.length === 0) return coords;
    const first = coords[0];
    const last = coords[coords.length - 1];
    // 检查首尾点是否相同（精度问题需处理，如保留6位小数）
    const isClosed = first[0] === last[0] && first[1] === last[1];
    if (!isClosed) {
        coords.push([...first]); // 复制第一个点到末尾
    }
    return coords;
}

const MapUtil = {
    WKTtoParsePoint,
    WKTtoParseLineString,
    WKTtoParsePolygon,
    pointToWKT,
    lineStringToWKT,
    polygonToWKT,
    circleToWKT,
    parseMultiPolygon,
    getPointsCenter,
    closePolygonCoords,
};
export default MapUtil;
